import type { UniversalCardProps } from 'pages/account/components/deposit-card/universal-card/types'
import { FormattedSavingStatus, IFormattedEarning } from 'store/fetchers/earnings/enum'

import { FC, useMemo } from 'react'
import { useRouter } from 'next/router'
import BigNumber from 'bignumber.js'

import { formatNumber } from 'utils/formatters'
import { useCurrency } from 'utils/use-currency'
import { compoundPeriodInterests } from 'utils/compound-period-interests'

import { UniversalCard } from 'pages/account/components/deposit-card/universal-card'
import { getFormattedDate, getFormattedTime } from 'pages/account/components/deposit-card/utils'

import { floorNumber } from '../../../../../utils/floor-number'

import { countDecimalPlaces } from '../../../../../utils/count-decimal-places'

import { getFormattedSavingStatus, savingCardColumns } from './utils'

interface SavingCardProps {
  key: string
  deposit: IFormattedEarning
}

export const SavingCard: FC<SavingCardProps> = ({ deposit }) => {
  const router = useRouter()

  const currency = useCurrency({
    code: deposit.currency_code,
    network: deposit.currency_network,
  })

  const earnedPlusDeposit = new BigNumber(deposit.deposit.amount).plus(deposit.earns)

  const trackDeposit = async () => {
    const statuses = ['NEW', 'CONFIRMED', 'DEPOSITING']

    if (statuses.includes(deposit.status)) {
      await router.push(deposit?.receive_from === 'internal_wallet'
        ? `/account/savings/success?earn_id=${deposit.earn_id}`
        : `/account/savings/send?earn_id=${deposit.earn_id}`)
    } else {
      await router.push(`/account/savings/info?earn_id=${deposit.earn_id}`)
    }
  }

  const formattedStatus = getFormattedSavingStatus(deposit.status)

  const cardInfo: UniversalCardProps = useMemo(
    () => ({
      columns: savingCardColumns,
      onTrackClick: trackDeposit,
      amountCell: {
        amount: formatNumber(floorNumber(+deposit.withdraw.available_amount, 2).toString()),
        ticker: currency?.code ?? deposit?.currency_code?.toUpperCase(),
        network: currency?.network ?? deposit?.currency_network?.toUpperCase(),
      },
      regularCell: [
        {
          mainText: getFormattedDate(deposit.created_at),
          secondaryText: getFormattedTime(deposit.created_at),
          date: true,
          key: 'date',
        },
        {
          mainText: isNaN(+compoundPeriodInterests(earnedPlusDeposit, deposit.percents.annual, 365, 365, false))
            ? '0'
            : countDecimalPlaces(
              +compoundPeriodInterests(earnedPlusDeposit, deposit.percents.annual, 365, 365, false),
            ) > 6
              ? formatNumber(
                Number(compoundPeriodInterests(earnedPlusDeposit, deposit.percents.annual, 365, 365, false)).toFixed(6),
              )
              : formatNumber(compoundPeriodInterests(earnedPlusDeposit, deposit.percents.annual, 365, 365, false)),
          secondaryText: currency?.code?.toUpperCase() ?? deposit?.currency_code?.toUpperCase(),
          key: 'annualReward',
        },
        {
          mainText:
            countDecimalPlaces(+deposit.total_earns) > 6
              ? formatNumber(Number(deposit.total_earns).toFixed(6))
              : formatNumber(deposit.total_earns),
          secondaryText: currency?.code?.toUpperCase() ?? deposit?.currency_code?.toUpperCase(),
          key: 'totalEarned',
        },
      ],
      statusCell: {
        status: formattedStatus,
        id: deposit.earn_id,
        createdAt: formattedStatus === FormattedSavingStatus.PENDING ? deposit.created_at : undefined,
      },
    }),
    [trackDeposit, currency],
  )

  return (
    <UniversalCard
      columns={cardInfo.columns}
      onTrackClick={cardInfo.onTrackClick}
      statusCell={cardInfo.statusCell}
      regularCell={cardInfo.regularCell}
      amountCell={cardInfo.amountCell}
      saving={deposit}
    />
  )
}
